import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, ShieldCheck, Mail, Smartphone, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaMethod, setMfaMethod] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const { login, setAuthFromTokens } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, remember_me: rememberMe })
      });
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        throw new Error('Server response ongeldig. Probeer opnieuw.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Inloggen mislukt');
      }
      
      if (data.mfa_required) {
        setMfaStep(true);
        setMfaMethod(data.mfa_method);
        setMfaToken(data.mfa_token);
        toast.info(data.mfa_method === 'email' 
          ? 'Verificatiecode verzonden naar uw e-mail' 
          : 'Voer de code van uw authenticator app in');
        return;
      }
      
      // Check if MFA setup is required (grace period expired)
      if (data.mfa_setup_required) {
        await setAuthFromTokens(data.access_token, data.refresh_token, data.user_type);
        // Don't force redirect to settings — let user start at dashboard with a warning banner
      }
      
      // Show warning if grace period is active but MFA not set up
      if (data.mfa_grace_days_remaining !== null && data.mfa_grace_days_remaining >= 0 && data.mfa_grace_days_remaining <= 7) {
        toast.warning(`MFA wordt verplicht over ${data.mfa_grace_days_remaining} dagen. Stel het nu in via Instellingen.`);
      }
      
      await setAuthFromTokens(data.access_token, data.refresh_token, data.user_type);
      
      // Use window.location for hard redirect to ensure auth state is fresh
      const userType = data.user_type || 'user';
      
      // Check if profile needs completion (first login or incomplete profile)
      if (data.profile_incomplete) {
        toast.info('Welkom! Vul eerst uw organisatieprofiel in.');
        window.location.href = '/organization';
        return;
      }
      
      toast.success('Welkom terug!');
      
      if (userType === 'admin') {
        window.location.href = '/admin';
      } else if (userType === 'partner') {
        window.location.href = '/partner/dashboard';
      } else {
        window.location.href = '/dashboard';
      }
    } catch (error) {
      toast.error(error.message || 'Inloggen mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-mfa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          mfa_token: mfaToken, 
          code: mfaCode 
        })
      });
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        throw new Error('Server response ongeldig. Probeer opnieuw.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verificatie mislukt');
      }
      
      await setAuthFromTokens(data.access_token, data.refresh_token, data.user_type);
      
      // Use window.location for hard redirect to ensure auth state is fresh
      const userType = data.user_type || 'user';
      
      // Check if profile needs completion (first login or incomplete profile)
      if (data.profile_incomplete) {
        toast.info('Welkom! Vul eerst uw organisatieprofiel in.');
        window.location.href = '/organization';
        return;
      }
      
      toast.success('Welkom terug!');
      
      if (userType === 'admin') {
        window.location.href = '/admin';
      } else if (userType === 'partner') {
        window.location.href = '/partner/dashboard';
      } else {
        window.location.href = '/dashboard';
      }
    } catch (error) {
      toast.error(error.message || 'Verificatie mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaStep(false);
    setMfaCode('');
    setMfaToken('');
    setMfaMethod('');
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden">
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(135deg, 
                #ff9a56 0%, 
                #ff6b8a 25%, 
                #c44eff 50%, 
                #7b68ee 75%, 
                #00bfff 100%
              )
            `,
            backgroundSize: '400% 400%',
            animation: 'gradientShift 15s ease infinite'
          }}
        />
        {/* Overlay waves */}
        <svg 
          className="absolute bottom-0 right-0 w-full h-full opacity-30"
          viewBox="0 0 1440 900" 
          preserveAspectRatio="xMaxYMax slice"
        >
          <path 
            fill="url(#wave1)" 
            d="M0,900 C300,700 600,800 900,600 C1200,400 1440,500 1440,0 L1440,900 Z"
          />
          <path 
            fill="url(#wave2)" 
            d="M0,900 C200,750 500,850 800,650 C1100,450 1440,550 1440,100 L1440,900 Z"
            opacity="0.7"
          />
          <defs>
            <linearGradient id="wave1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ff6b8a" />
              <stop offset="100%" stopColor="#c44eff" />
            </linearGradient>
            <linearGradient id="wave2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffd194" />
              <stop offset="100%" stopColor="#ff6b8a" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Header */}
      <header className="relative z-10 p-6">
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <img 
            src="https://spooker.io/wp-content/uploads/2022/12/logo.svg" 
            alt="Spooker" 
            className="h-8"
          />
        </Link>
      </header>

      {/* Login Card */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-120px)] px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            {/* Card Content */}
            <div className="p-8">
              {!mfaStep ? (
                <>
                  <h1 className="text-2xl font-semibold text-neutral-900 mb-6">
                    Aanmelden bij je account
                  </h1>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Email Field */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-neutral-700">
                        E-mail
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="h-12 border-2 border-neutral-200 focus:border-[#635bff] focus:ring-[#635bff] rounded-lg"
                        data-testid="login-email-input"
                      />
                    </div>

                    {/* Password Field */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="text-sm font-medium text-neutral-700">
                          Wachtwoord
                        </Label>
                        <Link
                          to="/forgot-password"
                          className="text-sm text-[#635bff] hover:text-[#4b45c6] transition-colors"
                          data-testid="forgot-password-link"
                        >
                          Je wachtwoord vergeten?
                        </Link>
                      </div>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="h-12 border-2 border-neutral-200 focus:border-[#635bff] focus:ring-[#635bff] rounded-lg pr-12"
                          data-testid="login-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>

                    {/* Remember Me */}
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="remember"
                        checked={rememberMe}
                        onCheckedChange={setRememberMe}
                        className="border-neutral-300 data-[state=checked]:bg-[#635bff] data-[state=checked]:border-[#635bff]"
                      />
                      <Label htmlFor="remember" className="text-sm text-neutral-600 cursor-pointer">
                        Onthoud mij op dit apparaat
                      </Label>
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      className="w-full h-12 bg-[#635bff] hover:bg-[#4b45c6] text-white font-medium rounded-lg transition-colors"
                      disabled={loading}
                      data-testid="login-submit-button"
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        'Aanmelden'
                      )}
                    </Button>
                  </form>

                  {/* Social Login Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-neutral-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-neutral-500">of</span>
                    </div>
                  </div>

                  {/* Social Login Buttons - Coming Soon */}
                  <div className="space-y-3">
                    <button
                      type="button"
                      disabled
                      className="w-full h-12 flex items-center justify-center gap-3 border-2 border-neutral-200 rounded-lg bg-neutral-50 text-neutral-400 cursor-not-allowed relative group"
                      data-testid="google-login-placeholder"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="currentColor"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      <span>Doorgaan met Google</span>
                      <span className="absolute right-3 text-[10px] bg-neutral-200 text-neutral-500 px-2 py-0.5 rounded-full">
                        Coming Soon
                      </span>
                    </button>

                    <button
                      type="button"
                      disabled
                      className="w-full h-12 flex items-center justify-center gap-3 border-2 border-neutral-200 rounded-lg bg-neutral-50 text-neutral-400 cursor-not-allowed relative group"
                      data-testid="microsoft-login-placeholder"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M11.4 24H0V12.6h11.4V24z" />
                        <path fill="currentColor" d="M24 24H12.6V12.6H24V24z" />
                        <path fill="currentColor" d="M11.4 11.4H0V0h11.4v11.4z" />
                        <path fill="currentColor" d="M24 11.4H12.6V0H24v11.4z" />
                      </svg>
                      <span>Doorgaan met Microsoft</span>
                      <span className="absolute right-3 text-[10px] bg-neutral-200 text-neutral-500 px-2 py-0.5 rounded-full">
                        Coming Soon
                      </span>
                    </button>
                  </div>
                </>
              ) : (
                /* MFA Verification */
                <>
                  <button 
                    onClick={handleBackToLogin}
                    className="flex items-center text-sm text-neutral-500 hover:text-neutral-700 transition-colors mb-6"
                    data-testid="mfa-back-button"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Terug
                  </button>
                  
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-12 w-12 rounded-full bg-[#635bff]/10 flex items-center justify-center">
                      {mfaMethod === 'email' ? (
                        <Mail className="h-6 w-6 text-[#635bff]" />
                      ) : (
                        <Smartphone className="h-6 w-6 text-[#635bff]" />
                      )}
                    </div>
                    <div>
                      <h1 className="text-xl font-semibold text-neutral-900">
                        Tweestapsverificatie
                      </h1>
                      <p className="text-sm text-neutral-500">
                        {mfaMethod === 'email' 
                          ? 'Voer de code uit uw e-mail in' 
                          : 'Voer de code uit uw authenticator app in'}
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleMfaVerify} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="mfa-code" className="text-sm font-medium text-neutral-700">
                        Verificatiecode
                      </Label>
                      <Input
                        id="mfa-code"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="000000"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        required
                        className="h-14 border-2 border-neutral-200 focus:border-[#635bff] focus:ring-[#635bff] rounded-lg text-center text-2xl tracking-[0.5em] font-mono"
                        data-testid="mfa-code-input"
                        autoComplete="one-time-code"
                      />
                      <p className="text-xs text-neutral-500 text-center">
                        {mfaMethod === 'email' 
                          ? 'De code is 10 minuten geldig' 
                          : 'De code wijzigt elke 30 seconden'}
                      </p>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 bg-[#635bff] hover:bg-[#4b45c6] text-white font-medium rounded-lg transition-colors"
                      disabled={loading || mfaCode.length !== 6}
                      data-testid="mfa-verify-button"
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          <ShieldCheck className="mr-2 h-5 w-5" />
                          Verificeren
                        </>
                      )}
                    </Button>
                  </form>

                  {mfaMethod === 'email' && (
                    <p className="text-center text-sm text-neutral-500 mt-4">
                      Geen code ontvangen?{' '}
                      <button
                        type="button"
                        onClick={handleBackToLogin}
                        className="text-[#635bff] hover:text-[#4b45c6]"
                      >
                        Opnieuw versturen
                      </button>
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Card Footer */}
            {!mfaStep && (
              <div className="px-8 py-4 bg-neutral-50 border-t border-neutral-100">
                <p className="text-center text-sm text-neutral-600">
                  Nieuw bij Spooker?{' '}
                  <Link
                    to="/register"
                    className="text-[#635bff] hover:text-[#4b45c6] font-medium"
                    data-testid="register-link"
                  >
                    Account aanvragen
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 p-6 flex items-center justify-between text-sm text-neutral-600">
        <span>&copy; 2026 Spooker</span>
        <div className="flex gap-4">
          <Link to="/privacy" className="hover:text-neutral-900">Privacy</Link>
          <Link to="/terms" className="hover:text-neutral-900">Voorwaarden</Link>
        </div>
      </footer>

      {/* CSS Animation */}
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
